import { useState } from "react";

/**
 * Small, dependency-free charts for PrepAI.
 *
 * Drawn to one scale per chart: ticks are "nice" round numbers up to the data's
 * maximum, every label names a value the chart reaches, and colours come from
 * theme tokens so text stays readable. Each chart has a "View as table" toggle
 * so the numbers are reachable without reading pixels.
 */

export function niceMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (step * magnitude >= value) return step * magnitude;
  }
  return 10 * magnitude;
}

export function compact(n: number): string {
  if (Math.abs(n) >= 1_000_000)
    return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 2).replace(/\.?0+$/, "")}m`;
  if (Math.abs(n) >= 1_000)
    return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1).replace(/\.0$/, "")}k`;
  return String(Math.round(n));
}

const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });

export interface Point {
  date: string;
  value: number | null;
}

export function LineChart({
  points,
  label,
  format = compact,
  color = "var(--color-c1)",
}: {
  points: Point[];
  label: string;
  format?: (n: number) => string;
  color?: string;
}) {
  const [table, setTable] = useState(false);
  const [hover, setHover] = useState<number | null>(null);
  const W = 640;
  const H = 200;
  const pad = { l: 44, r: 12, t: 12, b: 26 };
  const values = points.map((p) => p.value ?? 0);
  const max = niceMax(Math.max(0, ...values));
  const x = (i: number) =>
    pad.l + (points.length <= 1 ? 0 : (i / (points.length - 1)) * (W - pad.l - pad.r));
  const y = (v: number) => pad.t + (1 - v / max) * (H - pad.t - pad.b);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const path = points
    .map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.value ?? 0).toFixed(1)}`)
    .join(" ");
  const area = `${path} L${x(points.length - 1)},${y(0)} L${x(0)},${y(0)} Z`;
  const labelEvery = Math.max(1, Math.ceil(points.length / 6));
  const last = points[points.length - 1];

  return (
    <div>
      {table ? (
        <DataTable
          rows={points.map((p) => [shortDate(p.date), p.value === null ? "—" : format(p.value)])}
          headers={["Date", label]}
        />
      ) : (
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="h-auto w-full"
          role="img"
          aria-label={`${label} over time`}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={pad.l}
                x2={W - pad.r}
                y1={y(t)}
                y2={y(t)}
                stroke="var(--color-grid)"
                strokeWidth="1"
              />
              <text
                x={pad.l - 6}
                y={y(t) + 4}
                textAnchor="end"
                fontSize="11"
                fill="var(--color-text-muted)"
              >
                {format(t)}
              </text>
            </g>
          ))}
          {points.map((p, i) =>
            i % labelEvery === 0 || i === points.length - 1 ? (
              <text
                key={p.date}
                x={x(i)}
                y={H - 8}
                textAnchor="middle"
                fontSize="11"
                fill="var(--color-text-muted)"
              >
                {shortDate(p.date)}
              </text>
            ) : null,
          )}
          <path d={area} fill={color} opacity="0.12" />
          <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
          {last ? (
            <circle cx={x(points.length - 1)} cy={y(last.value ?? 0)} r="4" fill={color} />
          ) : null}
          {points.map((p, i) => (
            <rect
              key={p.date}
              x={x(i) - (W - pad.l - pad.r) / Math.max(1, points.length - 1) / 2}
              y={pad.t}
              width={(W - pad.l - pad.r) / Math.max(1, points.length - 1)}
              height={H - pad.t - pad.b}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
          {hover !== null && points[hover] ? (
            <g pointerEvents="none">
              <line
                x1={x(hover)}
                x2={x(hover)}
                y1={pad.t}
                y2={H - pad.b}
                stroke="var(--color-text-muted)"
                strokeDasharray="3 3"
              />
              <circle cx={x(hover)} cy={y(points[hover]!.value ?? 0)} r="4" fill={color} />
              <text
                x={Math.min(x(hover) + 8, W - 110)}
                y={pad.t + 14}
                fontSize="12"
                fill="var(--color-text)"
              >
                {shortDate(points[hover]!.date)}:{" "}
                {points[hover]!.value === null ? "hidden" : format(points[hover]!.value!)}
              </text>
            </g>
          ) : null}
        </svg>
      )}
      <ToggleTable table={table} onToggle={() => setTable((t) => !t)} />
    </div>
  );
}

export function BarChart({
  points,
  label,
  format = compact,
  color = "var(--color-c2)",
}: {
  points: Point[];
  label: string;
  format?: (n: number) => string;
  color?: string;
}) {
  const [table, setTable] = useState(false);
  const W = 640;
  const H = 200;
  const pad = { l: 48, r: 8, t: 12, b: 26 };
  const max = niceMax(Math.max(0, ...points.map((p) => p.value ?? 0)));
  const slot = (W - pad.l - pad.r) / Math.max(1, points.length);
  const y = (v: number) => pad.t + (1 - v / max) * (H - pad.t - pad.b);
  const labelEvery = Math.max(1, Math.ceil(points.length / 6));

  return (
    <div>
      {table ? (
        <DataTable
          rows={points.map((p) => [shortDate(p.date), p.value === null ? "—" : format(p.value)])}
          headers={["Date", label]}
        />
      ) : (
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="h-auto w-full"
          role="img"
          aria-label={`${label} per day`}
        >
          {[0, 0.5, 1].map((f) => (
            <g key={f}>
              <line
                x1={pad.l}
                x2={W - pad.r}
                y1={y(f * max)}
                y2={y(f * max)}
                stroke="var(--color-grid)"
              />
              <text
                x={pad.l - 6}
                y={y(f * max) + 4}
                textAnchor="end"
                fontSize="11"
                fill="var(--color-text-muted)"
              >
                {format(f * max)}
              </text>
            </g>
          ))}
          {points.map((p, i) => {
            const v = p.value ?? 0;
            return (
              <g key={p.date}>
                <rect
                  x={pad.l + i * slot + slot * 0.15}
                  y={y(v)}
                  width={slot * 0.7}
                  height={y(0) - y(v)}
                  fill={color}
                  rx="2"
                >
                  <title>
                    {shortDate(p.date)}: {format(v)}
                  </title>
                </rect>
                {i % labelEvery === 0 ? (
                  <text
                    x={pad.l + i * slot + slot / 2}
                    y={H - 8}
                    textAnchor="middle"
                    fontSize="11"
                    fill="var(--color-text-muted)"
                  >
                    {shortDate(p.date)}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
      )}
      <ToggleTable table={table} onToggle={() => setTable((t) => !t)} />
    </div>
  );
}

/** Horizontal bars for a ranked breakdown (subjects, payment methods). */
export function RankBars({
  items,
  format = compact,
  total,
}: {
  items: { key: string; value: number | null }[];
  format?: (n: number) => string;
  total?: number;
}) {
  const max = Math.max(1, ...items.map((i) => i.value ?? 0));
  if (!items.length) return <p className="text-[13px] text-text-muted">No data in this period.</p>;
  return (
    <ul className="flex flex-col gap-2">
      {items.map((item) => (
        <li
          key={item.key}
          className="grid grid-cols-[minmax(0,8rem)_1fr_auto] items-center gap-3 text-[13px]"
        >
          <span className="truncate">{item.key || "—"}</span>
          <span className="h-2 overflow-hidden rounded-full bg-surface-high">
            <span
              className="block h-full rounded-full bg-c1"
              style={{ width: `${((item.value ?? 0) / max) * 100}%` }}
            />
          </span>
          <span className="tabular-nums text-text-muted">
            {item.value === null ? (
              <span title="Fewer than 5 students — withheld so no child can be singled out">
                Under 5
              </span>
            ) : total ? (
              `${Math.round(((item.value ?? 0) / total) * 100)}%`
            ) : (
              format(item.value)
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ToggleTable({ table, onToggle }: { table: boolean; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} className="mt-1 text-[11px] text-accent-light">
      {table ? "View as chart" : "View as table"}
    </button>
  );
}

export function DataTable({ headers, rows }: { headers: string[]; rows: (string | number)[][] }) {
  return (
    <div className="max-h-64 overflow-auto rounded border border-border">
      <table className="w-full text-[12px]">
        <thead className="sticky top-0 bg-surface-high">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-3 py-1.5 text-left font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-border">
              {row.map((cell, j) => (
                <td key={j} className={`px-3 py-1 ${j ? "tabular-nums" : ""}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
